import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/AppIcon';
import axios from 'axios';

const SellerProfileScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId: paramUserId } = useParams();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [sellerProducts, setSellerProducts] = useState([]);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('products');
  const [userToken, setUserToken] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedContactType, setSelectedContactType] = useState(null);
  const [contactValue, setContactValue] = useState('');

  // Récupérer depuis state OU params OU localStorage
  const [userId, setUserId] = useState(() => {
    if (location.state?.userId) return location.state.userId;
    if (paramUserId) return paramUserId;
    return localStorage.getItem('lastViewedSellerId') || null;
  });

  const [userName, setUserName] = useState(() => {
    if (location.state?.userName) return location.state.userName;
    return localStorage.getItem('lastViewedSellerName') || null;
  });

  useEffect(() => {
    loadUserToken();
  }, []);

  useEffect(() => {
    // Sauvegarder dans localStorage pour persistance
    if (userId) {
      localStorage.setItem('lastViewedSellerId', userId);
    }
    if (userName) {
      localStorage.setItem('lastViewedSellerName', userName);
    }
  }, [userId, userName]);

  useEffect(() => {
    if (userToken && userId) {
      fetchSellerData();
    }
  }, [userId, userToken]);

  const loadUserToken = () => {
    try {
      const token = localStorage.getItem('userToken');
      setUserToken(token);
    } catch (error) {
      console.error('Error loading user token:', error);
    }
  };

  const getDefaultAvatar = (name) => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
    const nameToUse = name || t('user');
    const colorIndex = nameToUse.charCodeAt(0) % colors.length;
    
    const initials = nameToUse
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
    
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${colors[colorIndex].replace('#', '')}&color=fff&size=256`;
  };

  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (userToken) {
      headers['Authorization'] = `Bearer ${userToken}`;
    }
    
    return headers;
  };

  const fetchSellerData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!userId) {
        throw new Error(t('errors.missingUserId'));
      }

      // Récupérer les infos du vendeur
      const userResponse = await axios.get(`https://api.autocasses.com/api/user/get/${userId}`, {
        headers: getAuthHeaders(),
        timeout: 10000,
      });

      if (userResponse.data) {
        setSellerInfo(userResponse.data);
      } else {
        throw new Error(t('errors.noUserData'));
      }

      // Récupérer ses produits
      const productsResponse = await axios.get(`https://api.autocasses.com/api/produit/getproduits/${userId}`, {
        headers: getAuthHeaders(),
        timeout: 10000,
      });
      
      const productsData = productsResponse.data;
      
      if (Array.isArray(productsData)) {
        const transformedProducts = productsData.map((produit) => {
          const createdAt = new Date(produit.createdAt);
          const now = new Date();
          const diffHours = Math.floor((now - createdAt) / (1000 * 60 * 60));
          
          let timeAgo;
          if (diffHours < 24) {
            timeAgo = t('timeAgo.hours', { count: diffHours });
          } else {
            const diffDays = Math.floor(diffHours / 24);
            timeAgo = t('timeAgo.days', { count: diffDays });
          }
          
          const getCondition = (etat) => {
            const etatLower = etat ? etat.toLowerCase() : '';
            
            switch(etatLower) {
              case 'new':
              case 'neuf': return t('conditions.new');
              case 'like_new':
              case 'comme neuf': return t('conditions.likeNew');
              case 'used':
              case 'occasion': return t('conditions.used');
              case 'bon etat': return t('conditions.good');
              case 'excellent etat': return t('conditions.excellent');
              default: return t('conditions.new');
            }
          };
          
          let photoUrl;
          if (produit.photos && produit.photos.length > 0) {
            photoUrl = produit.photos[0].photo_url;
          } else {
            const defaultImages = [
              "https://images.unsplash.com/photo-1580273916550-e323be2ae537",
              "https://images.unsplash.com/photo-1555212697-194d92e88b52",
              "https://images.unsplash.com/photo-1505740420928-5e560c06d30e",
            ];
            const randomIndex = Math.floor(Math.random() * defaultImages.length);
            photoUrl = defaultImages[randomIndex];
          }
          
          const isNew = diffHours < 24;
          const isPremium = produit.garantie && produit.garantie.trim() !== '' && produit.prix > 500000;
          
          return {
            id: produit.id,
            title: produit.titre,
            price: produit.prix,
            currency: 'FCFA',
            image: photoUrl,
            condition: getCondition(produit.etat),
            location: `${produit.city || ''}`.trim(),
            timeAgo: timeAgo,
            isPremium: isPremium,
            isNew: isNew,
            negociable: produit.negociable === 'true' || produit.negociable === true,
            echange: produit.echange === 'true' || produit.echange === true,
            garantie: produit.garantie || '',
            description: produit.description || '',
            createdAt: produit.createdAt,
            photos: produit.photos || []
          };
        });
        
        // Trier par date de création (les plus récentes d'abord)
        transformedProducts.sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        
        setSellerProducts(transformedProducts);
      } else {
        throw new Error(t('errors.invalidProductFormat'));
      }
    } catch (error) {
      console.error('Error fetching seller data:', error);
      
      let errorMessage = t('errors.loadingError');
      
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = t('errors.sessionExpired');
        } else if (error.response.status === 403) {
          errorMessage = t('errors.unauthorized');
        } else if (error.response.status === 404) {
          errorMessage = t('errors.userNotFound');
        }
      } else if (error.request) {
        errorMessage = t('errors.noConnection');
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchSellerData();
  };

  const handleManualRefresh = () => {
    setRefreshing(true);
    fetchSellerData();
  };

  const formatPrice = (price) => {
    const priceNum = parseInt(price);
    if (isNaN(priceNum)) return '0';
    
    return priceNum.toLocaleString('fr-CM', {
      minimumFractionDigits: 0
    });
  };

  const handleProductClick = (product) => {
     navigate('/product', { 
      state: { 
        productId: product.id,
        productData: product
      }
    });
  };

  const handleContact = (type, value) => {
    if (!value) return;
    
    setSelectedContactType(type);
    setContactValue(value);
    setShowContactModal(true);
  };

  const confirmContact = () => {
    switch(selectedContactType) {
      case 'phone':
        window.location.href = `tel:${contactValue}`;
        break;
      case 'whatsapp':
        const cleanPhoneNumber = contactValue.replace(/\s/g, '');
        const message = t('whatsappMessage');
        window.open(`https://wa.me/${cleanPhoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:${contactValue}`;
        break;
      case 'website':
        let url = contactValue;
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }
        window.open(url, '_blank');
        break;
      case 'address':
        const encodedAddress = encodeURIComponent(contactValue);
        window.open(`https://maps.google.com/?q=${encodedAddress}`, '_blank');
        break;
    }
    setShowContactModal(false);
  };

  const closeContactModal = () => {
    setShowContactModal(false);
  };

  const renderProductItem = (item) => {
    return (
      <div
        key={item.id}
        className="product-item group cursor-pointer hover:shadow-xl transition-all duration-300 bg-white rounded-lg overflow-hidden border border-gray-200"
        onClick={() => handleProductClick(item)}
      >
        <div className="relative aspect-square overflow-hidden">
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
          {item.isNew && (
            <div className="absolute top-3 left-3 bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">
              {t('badges.new')}
            </div>
          )}
          {item.isPremium && (
            <div className="absolute top-3 right-3 bg-amber-500 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
              <Icon name="Star" size={10} />
              {t('badges.premium')}
            </div>
          )}
        </div>
        
        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2 min-h-[40px]">
            {item.title}
          </h3>
          
          <div className="flex items-center justify-between mb-3">
            <span className="text-lg font-bold text-blue-600">
              {formatPrice(item.price)} {item.currency}
            </span>
            {item.negociable && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium">
                {t('badges.negotiable')}
              </span>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
              <Icon name="CheckCircle" size={12} />
              <span className="truncate">{item.condition}</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
              <Icon name="MapPin" size={12} />
              <span className="truncate">{item.location}</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1">
              <Icon name="Clock" size={12} />
              <span>{item.timeAgo}</span>
            </div>
            {item.echange && (
              <span className="text-amber-600 font-medium">{t('badges.exchange')}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyProducts = () => (
    <div className="col-span-full text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
      <Icon name="Package" size={64} className="mx-auto text-gray-300 mb-4" />
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        {t('seller.noListings')}
      </h3>
      <p className="text-gray-600 max-w-md mx-auto">
        {t('seller.noProductsMessage')}
      </p>
    </div>
  );

  const renderContactModal = () => {
    if (!showContactModal) return null;

    const getModalContent = () => {
      switch(selectedContactType) {
        case 'phone':
          return {
            title: t('alerts.confirmCall'),
            message: `${t('alerts.callMessage')} ${contactValue}?`,
            icon: 'Phone',
            color: 'blue',
            confirmText: t('buttons.call')
          };
        case 'whatsapp':
          return {
            title: t('alerts.confirmWhatsApp'),
            message: `${t('alerts.whatsappMessage')} ${contactValue}?`,
            icon: 'MessageCircle',
            color: 'green',
            confirmText: 'WhatsApp'
          };
        case 'email':
          return {
            title: t('alerts.confirmEmail'),
            message: `${t('alerts.emailMessage')} ${contactValue}?`,
            icon: 'Mail',
            color: 'purple',
            confirmText: t('buttons.email')
          };
        case 'website':
          return {
            title: t('alerts.confirmWebsite'),
            message: `${t('alerts.websiteMessage')}?`,
            icon: 'Globe',
            color: 'indigo',
            confirmText: t('buttons.visit')
          };
        case 'address':
          return {
            title: t('alerts.confirmAddress'),
            message: `${t('alerts.addressMessage')}?`,
            icon: 'MapPin',
            color: 'red',
            confirmText: t('buttons.openMaps')
          };
        default:
          return {
            title: t('alerts.confirmAction'),
            message: t('alerts.confirmContinue'),
            icon: 'Info',
            color: 'blue',
            confirmText: t('buttons.continue')
          };
      }
    };

    const content = getModalContent();

    return (
      <div 
        className="fixed inset-0 z-50 bg-black bg-opacity-40 animate-fade-in flex items-center justify-center p-4"
        onClick={closeContactModal}
      >
        <div 
          className="bg-white rounded-xl shadow-xl animate-slide-up-sm w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-${content.color}-50`}>
                <Icon 
                  name={content.icon} 
                  size={24} 
                  className={`text-${content.color}-600`} 
                />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{content.title}</h3>
                <p className="text-gray-600">{content.message}</p>
                {contactValue && selectedContactType !== 'address' && selectedContactType !== 'website' && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">{contactValue}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={closeContactModal}
                className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('buttons.cancel')}
              </button>
              <button
                onClick={confirmContact}
                className={`flex-1 px-4 py-3 font-semibold rounded-lg transition-colors bg-${content.color}-600 text-white hover:bg-${content.color}-700`}
              >
                {content.confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !refreshing) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600">{t('loadings.profile')}</p>
      </div>
    );
  }

  if (error && !sellerInfo && sellerProducts.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <Icon name="AlertCircle" size={60} className="text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('errors.error')}</h2>
        <p className="text-gray-600 text-center mb-6">{error}</p>
        
        <div className="flex flex-col gap-3">
          <button
            onClick={handleManualRefresh}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            {t('buttons.retry')}
          </button>
          
          {error.includes(t('errors.sessionExpired')) && (
            <button
              onClick={() => navigate('/auth')}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
            >
              {t('buttons.login')}
            </button>
          )}
          
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold"
          >
            {t('buttons.goBack')}
          </button>
        </div>
      </div>
    );
  }

  const sellerName = sellerInfo?.nom || userName || t('seller.defaultName');
  const sellerAvatar = sellerInfo?.avatar 
    ? (sellerInfo.avatar.startsWith('http') 
        ? sellerInfo.avatar 
        : `https://api.autocasses.com/uploads/avatars/${sellerInfo.avatar}`)
    : getDefaultAvatar(sellerName);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Indicateur de rafraîchissement */}
      {refreshing && (
        <div className="fixed top-4 right-4 z-40 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-pulse">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          <span className="text-sm">{t('loadings.refreshing')}</span>
        </div>
      )}
      
      {/* Header avec dégradé */}
      <div className="relative h-48 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="container mx-auto px-4 h-full flex items-center">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <Icon name="ArrowLeft" size={24} className="text-white" />
              </button>
              <h1 className="text-2xl font-bold text-white">
                {t('titles.sellerProfile')}
              </h1>
            </div>
            
            {/* Bouton de rafraîchissement */}
            <button
              onClick={handleManualRefresh}
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              title={t('buttons.refresh')}
            >
              <Icon name="RefreshCw" size={20} className="text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Colonne de gauche - Informations du vendeur */}
          <div className="lg:col-span-1 space-y-6">
            {/* Carte d'informations vendeur */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              {/* En-tête vendeur */}
              <div className="flex flex-col items-center mb-6">
                <div className="relative mb-4">
                  <img
                    src={sellerAvatar}
                    alt={sellerName}
                    className="w-32 h-32 rounded-full border-4 border-white shadow-lg"
                  />
                  <div className="absolute -bottom-2 -right-2 flex gap-2">
                    {sellerInfo?.isVerified && (
                      <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center border-2 border-white">
                        <Icon name="Check" size={14} className="text-white" />
                      </div>
                    )}
                    {sellerInfo?.isPro && (
                      <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center border-2 border-white">
                        <span className="text-xs font-bold text-white">Pro</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {sellerName}
                    </h2>
                    {sellerInfo?.type_pro && sellerInfo.type_pro !== 'null' && (
                      <span className="bg-blue-100 text-blue-700 text-sm font-semibold px-3 py-1 rounded-full">
                        {sellerInfo.type_pro === 'garage' 
                          ? t('sellerTypes.garage')
                          : sellerInfo.type_pro === 'mecanicien'
                          ? t('sellerTypes.mechanic')
                          : sellerInfo.type_pro}
                      </span>
                    )}
                  </div>
                  
                  {sellerInfo?.adresse && sellerInfo.adresse !== t('seller.unspecifiedAddress') && (
                    <button
                      onClick={() => handleContact('address', sellerInfo.adresse)}
                      className="flex items-center justify-center gap-1 text-gray-600 hover:text-blue-600 transition-colors mx-auto"
                    >
                      <Icon name="MapPin" size={16} />
                      <span className="text-sm truncate max-w-xs">{sellerInfo.adresse}</span>
                      <Icon name="ExternalLink" size={14} />
                    </button>
                  )}
                  
                  <div className="flex items-center justify-center gap-8 mt-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{sellerProducts.length}</div>
                      <div className="text-sm text-gray-500">{t('seller.listings')}</div>
                    </div>
                    <div className="w-px h-8 bg-gray-200"></div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">
                        {sellerInfo?.createdAt 
                          ? new Date(sellerInfo.createdAt).getFullYear()
                          : new Date().getFullYear()}
                      </div>
                      <div className="text-sm text-gray-500">{t('seller.memberSince')}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Boutons d'action rapide */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button
                  onClick={() => handleContact('phone', sellerInfo?.telephone)}
                  disabled={!sellerInfo?.telephone}
                  className={`flex items-center justify-center gap-2 p-3 rounded-xl transition-colors ${
                    sellerInfo?.telephone
                      ? 'bg-blue-50 hover:bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Icon name="Phone" size={20} />
                  <span className="font-semibold">{t('buttons.call')}</span>
                </button>
                
                <button
                  onClick={() => handleContact('whatsapp', sellerInfo?.telephone)}
                  disabled={!sellerInfo?.telephone}
                  className={`flex items-center justify-center gap-2 p-3 rounded-xl transition-colors ${
                    sellerInfo?.telephone
                      ? 'bg-green-50 hover:bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Icon name="MessageCircle" size={20} />
                  <span className="font-semibold">WhatsApp</span>
                </button>
                
                <button
                  onClick={() => handleContact('email', sellerInfo?.email)}
                  disabled={!sellerInfo?.email}
                  className={`flex items-center justify-center gap-2 p-3 rounded-xl transition-colors ${
                    sellerInfo?.email
                      ? 'bg-purple-50 hover:bg-purple-100 text-purple-600'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Icon name="Mail" size={20} />
                  <span className="font-semibold">{t('buttons.message')}</span>
                </button>

                <button
                  onClick={() => setActiveTab('info')}
                  className="flex items-center justify-center gap-2 p-3 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl transition-colors"
                >
                  <Icon name="Info" size={20} />
                  <span className="font-semibold">{t('tabs.contact')}</span>
                </button>
              </div>

              {/* Description du vendeur */}
              {sellerInfo?.descriptions && sellerInfo.descriptions !== 'null' && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="FileText" size={18} className="text-blue-600" />
                    <h3 className="font-semibold text-gray-900">{t('sections.description')}</h3>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-line">{sellerInfo.descriptions}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Informations professionnelles */}
            {sellerInfo?.isPro && (
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Building" size={20} className="text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">{t('sections.professionalInfo')}</h3>
                </div>
                
                <div className="space-y-4">
                  {sellerInfo?.nom_pro && sellerInfo.nom_pro !== 'null' && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Icon name="Building" size={18} className="text-blue-600" />
                      <div>
                        <div className="text-sm text-gray-500">{t('seller.business')}</div>
                        <div className="font-medium text-gray-900">{sellerInfo.nom_pro}</div>
                      </div>
                    </div>
                  )}
                  
                  {sellerInfo?.tel_pro && sellerInfo.tel_pro !== 'null' && (
                    <button
                      onClick={() => handleContact('phone', sellerInfo.tel_pro)}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors text-left w-full"
                    >
                      <Icon name="Phone" size={18} className="text-blue-600" />
                      <div className="flex-1">
                        <div className="text-sm text-gray-500">{t('seller.businessPhone')}</div>
                        <div className="font-medium text-blue-600">{sellerInfo.tel_pro}</div>
                      </div>
                      <Icon name="ArrowRight" size={18} className="text-blue-600" />
                    </button>
                  )}
                  
                  {sellerInfo?.businessEmail && sellerInfo.businessEmail !== 'null' && (
                    <button
                      onClick={() => handleContact('email', sellerInfo.businessEmail)}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-purple-50 transition-colors text-left w-full"
                    >
                      <Icon name="Mail" size={18} className="text-purple-600" />
                      <div className="flex-1">
                        <div className="text-sm text-gray-500">{t('seller.businessEmail')}</div>
                        <div className="font-medium text-purple-600 truncate">{sellerInfo.businessEmail}</div>
                      </div>
                      <Icon name="ArrowRight" size={18} className="text-purple-600" />
                    </button>
                  )}
                  
                  {sellerInfo?.businessWebsite && sellerInfo.businessWebsite !== 'null' && (
                    <button
                      onClick={() => handleContact('website', sellerInfo.businessWebsite)}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-indigo-50 transition-colors text-left w-full"
                    >
                      <Icon name="Globe" size={18} className="text-indigo-600" />
                      <div className="flex-1">
                        <div className="text-sm text-gray-500">{t('seller.website')}</div>
                        <div className="font-medium text-indigo-600 truncate">{sellerInfo.businessWebsite}</div>
                      </div>
                      <Icon name="ArrowRight" size={18} className="text-indigo-600" />
                    </button>
                  )}
                  
                  {sellerInfo?.businessAddress && sellerInfo.businessAddress !== 'null' && (
                    <button
                      onClick={() => handleContact('address', sellerInfo.businessAddress)}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-red-50 transition-colors text-left w-full"
                    >
                      <Icon name="MapPin" size={18} className="text-red-600" />
                      <div className="flex-1">
                        <div className="text-sm text-gray-500">{t('seller.businessAddress')}</div>
                        <div className="font-medium text-red-600 line-clamp-2">{sellerInfo.businessAddress}</div>
                      </div>
                      <Icon name="ArrowRight" size={18} className="text-red-600" />
                    </button>
                  )}
                  
                  {sellerInfo?.specialites && sellerInfo.specialites !== 'null' && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Icon name="Award" size={18} className="text-green-600" />
                      <div>
                        <div className="text-sm text-gray-500">{t('seller.specialties')}</div>
                        <div className="font-medium text-gray-900">{sellerInfo.specialites}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Colonne de droite - Contenu principal */}
          <div className="lg:col-span-2">
            {/* Onglets pour mobile */}
            <div className="lg:hidden bg-white rounded-xl shadow-sm mb-6">
              <div className="flex border-b">
                <button
                  onClick={() => setActiveTab('products')}
                  className={`flex-1 py-3 flex items-center justify-center gap-2 font-semibold ${
                    activeTab === 'products'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500'
                  }`}
                >
                  <Icon name="Package" size={18} />
                  {t('tabs.products')}
                  {sellerProducts.length > 0 && (
                    <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">
                      {sellerProducts.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('info')}
                  className={`flex-1 py-3 flex items-center justify-center gap-2 font-semibold ${
                    activeTab === 'info'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-500'
                  }`}
                >
                  <Icon name="Info" size={18} />
                  {t('tabs.contact')}
                </button>
              </div>
            </div>

            {/* Contenu des onglets */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              {activeTab === 'products' ? (
                <div>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {t('seller.allProducts', { count: sellerProducts.length })}
                    </h2>
                    <p className="text-gray-600 mt-2">
                      {t('seller.clickForDetails')}
                    </p>
                  </div>
                  
                  {sellerProducts.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {sellerProducts.map((product) => renderProductItem(product))}
                    </div>
                  ) : (
                    renderEmptyProducts()
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-6">
                    <Icon name="Contact" size={20} className="text-blue-600" />
                    <h2 className="text-2xl font-bold text-gray-900">{t('sections.contactInfo')}</h2>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <Icon name="User" size={20} className="text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">{t('seller.fullName')}</div>
                          <div className="text-lg font-medium text-gray-900">{sellerName}</div>
                        </div>
                      </div>
                    </div>
                    
                    {sellerInfo?.email && (
                      <button
                        onClick={() => handleContact('email', sellerInfo.email)}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors w-full text-left"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                            <Icon name="Mail" size={20} className="text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm text-gray-500">Email</div>
                            <div className="text-lg font-medium text-blue-600 truncate">{sellerInfo.email}</div>
                          </div>
                        </div>
                        <Icon name="ExternalLink" size={18} className="text-blue-600" />
                      </button>
                    )}
                    
                    {sellerInfo?.telephone && (
                      <button
                        onClick={() => handleContact('phone', sellerInfo.telephone)}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors w-full text-left"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                            <Icon name="Phone" size={20} className="text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm text-gray-500">{t('seller.phone')}</div>
                            <div className="text-lg font-medium text-blue-600">{sellerInfo.telephone}</div>
                          </div>
                        </div>
                        <Icon name="ExternalLink" size={18} className="text-blue-600" />
                      </button>
                    )}
                    
                    {sellerInfo?.adresse && sellerInfo.adresse !== t('seller.unspecifiedAddress') && (
                      <button
                        onClick={() => handleContact('address', sellerInfo.adresse)}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors w-full text-left"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                            <Icon name="MapPin" size={20} className="text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm text-gray-500">{t('seller.address')}</div>
                            <div className="text-lg font-medium text-blue-600 line-clamp-2">{sellerInfo.adresse}</div>
                          </div>
                        </div>
                        <Icon name="ExternalLink" size={18} className="text-blue-600" />
                      </button>
                    )}
                    
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <Icon name="Calendar" size={20} className="text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">{t('seller.memberSince')}</div>
                          <div className="text-lg font-medium text-gray-900">
                            {sellerInfo?.createdAt 
                              ? new Date(sellerInfo.createdAt).toLocaleDateString('fr-FR', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })
                              : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de contact */}
      {renderContactModal()}

      {/* Styles CSS */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp-sm {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-slide-up-sm {
          animation: slideUp-sm 0.3s ease-out;
        }
        
        .aspect-square {
          aspect-ratio: 1 / 1;
        }
        
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        
        .whitespace-pre-line {
          white-space: pre-line;
        }
        
        .product-item {
          transition: all 0.3s ease;
        }
        
        .product-item:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
};

export default SellerProfileScreen;